import React, { useEffect, useState, useRef } from "react";
import "./ProjectsPage.css";
import { Pencil, Search, Plus, X, Edit3, Trash2, Minus } from "lucide-react";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    client_id: "",
    manager_id: "",
    description: "",
    start_date: "",
    deadline: "",
    budget: "",
    advance_paid: "",
    currency: "",
    status: "planned",
    upsellings: "",
  });
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const modalRef = useRef(null);
  const detailRef = useRef(null);
  const messageTimeoutRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [assetForm, setAssetForm] = useState({
    name: "",
    provider: "",
    purchase_date: "",
    expiry_date: "",
    cost: "",
    currency: "",
    status: "active",
    notes: "",
  });
  const [assetSaving, setAssetSaving] = useState(false);
  const [assetFormOpen, setAssetFormOpen] = useState(true);

  const user = JSON.parse(localStorage.getItem("user")) || {};

  function formatCurrency(amount, currency) {
    if (amount == null || amount === "") return "-";
    const num = Number(amount);
    if (Number.isNaN(num)) return String(amount);
    try {
      if (currency) {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          maximumFractionDigits: 2,
        }).format(num);
      }
    } catch (err) {}
    return new Intl.NumberFormat().format(num);
  }

  async function apiFetch(url, opts = {}) {
    const token = localStorage.getItem("token");
    const headers = { ...(opts.headers || {}) };
    if (opts.json !== false && opts.body !== undefined) {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(url, { ...opts, headers });
    const contentType = res.headers.get("content-type") || "";
    let parsed;
    try {
      parsed = contentType.includes("application/json")
        ? await res.json()
        : await res.text();
    } catch (err) {
      parsed = `Invalid response body: ${err.message}`;
    }
    return { ok: res.ok, status: res.status, body: parsed, contentType };
  }

  async function fetchProjects() {
    setLoading(true);
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/projects`,
        {
          method: "GET",
          json: false,
        }
      );
      if (!ok) throw new Error(body?.error || "Failed to fetch projects");
      setProjects(Array.isArray(body) ? body : []);
    } catch (err) {
      setMessage(`❌ ${err.message}`);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchClients() {
    try {
      const { ok, body } = await apiFetch(`${process.env.REACT_APP_API_URL}/api/clients`, {
        method: "GET",
        json: false,
      });
      if (!ok) throw new Error(body?.error || "Failed to fetch clients");
      setClients(Array.isArray(body) ? body : []);
    } catch (err) {
      setClients([]);
      console.error(err.message);
    }
  }

  async function fetchManagers() {
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/users?role=manager`,
        { method: "GET", json: false }
      );
      if (!ok) throw new Error(body?.error || "Failed to fetch managers");
      setManagers(Array.isArray(body) ? body : []);
    } catch (err) {
      setManagers([]);
      console.error(err.message);
    }
  }

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchManagers();
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function showTempMessage(text) {
    setMessage(text);
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = setTimeout(() => setMessage(""), 4000);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const url = editingProject
        ? `${process.env.REACT_APP_API_URL}/api/projects/${editingProject.id}`
        : `${process.env.REACT_APP_API_URL}/api/projects`;
      const method = editingProject ? "PUT" : "POST";

      const payload = {
        name: form.name || null,
        client_id: form.client_id ? Number(form.client_id) : null,
        manager_id: form.manager_id ? Number(form.manager_id) : null,
        description: form.description || null,
        start_date: form.start_date || null,
        deadline: form.deadline || null,
        budget: form.budget !== "" ? Number(form.budget) : null,
        advance_paid:
          form.advance_paid !== "" ? Number(form.advance_paid) : null,
        currency: form.currency || null,
        status: form.status || "planned",
        upsellings: form.upsellings || null,
      };

      const { ok, body } = await apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (!ok) throw new Error(body?.error || "Failed to save project");

      await fetchProjects();
      showTempMessage(
        editingProject ? "✅ Project updated!" : "✅ Project added!"
      );
      setShowModal(false);
      setEditingProject(null);
      resetForm();
    } catch (err) {
      showTempMessage(`❌ ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${id}`,
        { method: "DELETE", json: false }
      );
      if (!ok) throw new Error(body?.error || "Failed to delete project");
      await fetchProjects();
      showTempMessage("✅ Project deleted");
    } catch (err) {
      alert(`❌ ${err.message}`);
    }
  }

  function handleEdit(project) {
    setEditingProject(project);
    setForm({
      name: project.name || "",
      client_id: project.client_id != null ? String(project.client_id) : "",
      manager_id: project.manager_id != null ? String(project.manager_id) : "",
      description: project.description || "",
      start_date: project.start_date ? project.start_date.split("T")[0] : "",
      deadline: project.deadline ? project.deadline.split("T")[0] : "",
      budget: project.budget != null ? String(project.budget) : "",
      advance_paid:
        project.advance_paid != null ? String(project.advance_paid) : "",
      currency: project.currency || "",
      status: project.status || "planned",
      upsellings: project.upsellings || "",
    });
    setShowModal(true);
  }

  function resetForm() {
    setForm({
      name: "",
      client_id: "",
      manager_id: user?.role === "manager" ? String(user.id) : "",
      description: "",
      start_date: "",
      deadline: "",
      budget: "",
      advance_paid: "",
      currency: "",
      status: "planned",
      upsellings: "",
    });
  }

  function handleOverlayClick(e) {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setShowModal(false);
      setEditingProject(null);
      resetForm();
    }
  }

  function openProjectDetail(project) {
    setSelectedProject(project);
    setShowDetail(true);
    fetchAssets(project.id);
    setAssetFormOpen(false);
  }

  function handleDetailOverlay(e) {
    if (detailRef.current && !detailRef.current.contains(e.target)) {
      setShowDetail(false);
      setSelectedProject(null);
      setAssets([]);
      setAssetForm({
        name: "",
        provider: "",
        purchase_date: "",
        expiry_date: "",
        cost: "",
        currency: "",
        status: "active",
        notes: "",
      });
    }
  }

  async function fetchAssets(projectId) {
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/project-assets?project_id=${projectId}`,
        { method: "GET", json: false }
      );
      if (!ok) throw new Error(body?.error || "Failed to fetch assets");
      setAssets(Array.isArray(body) ? body : []);
    } catch (err) {
      console.error(err.message);
      setAssets([]);
    }
  }

  function handleAssetChange(e) {
    const { name, value } = e.target;
    setAssetForm((f) => ({ ...f, [name]: value }));
  }

  async function createAsset(e) {
    e.preventDefault();
    if (!selectedProject) return;
    setAssetSaving(true);
    try {
      const payload = {
        project_id: selectedProject.id,
        name: assetForm.name || "",
        provider: assetForm.provider || null,
        purchase_date: assetForm.purchase_date || null,
        expiry_date: assetForm.expiry_date || null,
        cost: assetForm.cost !== "" ? Number(assetForm.cost) : null,
        currency: assetForm.currency || null,
        status: assetForm.status || "active",
        notes: assetForm.notes || null,
      };
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/project-assets`,
        { method: "POST", body: JSON.stringify(payload) }
      );
      if (!ok) throw new Error(body?.error || "Failed to create asset");
      await fetchAssets(selectedProject.id);
      setAssetForm({
        name: "",
        provider: "",
        purchase_date: "",
        expiry_date: "",
        cost: "",
        currency: "",
        status: "active",
        notes: "",
      });
      showTempMessage("✅ Asset added");
    } catch (err) {
      showTempMessage(`❌ ${err.message}`);
    } finally {
      setAssetSaving(false);
    }
  }

  async function deleteAsset(id) {
    if (!window.confirm("Delete this asset?")) return;
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/project-assets/${id}`,
        { method: "DELETE", json: false }
      );
      if (!ok) throw new Error(body?.error || "Failed to delete asset");
      if (selectedProject) await fetchAssets(selectedProject.id);
      showTempMessage("✅ Asset deleted");
    } catch (err) {
      showTempMessage(`❌ ${err.message}`);
    }
  }

  const filteredProjects = projects.filter((p) => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.status || "").toLowerCase().includes(q) ||
      (p.client_id != null ? String(p.client_id) : "").includes(q)
    );
  });

  return (
    <div className="projects-container">
      <div className="projects-header-row">
        <h2>Nam-Mark Projects</h2>
        <div className="projects-header-actions">
          <div className="projects-search-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search employees..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <button
            className="projects-add-btn"
            onClick={() => {
              resetForm();
              setEditingProject(null);
              setShowModal(true);
            }}
          >
            <Plus size={16} /> Add Project
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading projects...</p>
      ) : (
        <div className="projects-table-wrapper">
          <table className="projects-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client</th>
                <th>Description</th>
                <th>Budget</th>
                <th>Total Received</th>
                <th>Deadline</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length > 0 ? (
                filteredProjects.map((p) => (
                  <tr key={p.id} className="projects-row">
                    <td onClick={() => openProjectDetail(p)}>{p.name}</td>
                    <td onClick={() => openProjectDetail(p)}>
                      {clients.find((c) => c.id === p.client_id)?.name || "-"}
                    </td>
                    <td
                      onClick={() => openProjectDetail(p)}
                      className="projects-desc-cell"
                    >
                      {p.description
                        ? p.description.slice(0, 80) +
                          (p.description.length > 80 ? "..." : "")
                        : "-"}
                    </td>
                    <td onClick={() => openProjectDetail(p)}>
                      {p.budget != null ? p.budget : "-"}
                    </td>

                    <td onClick={() => openProjectDetail(p)}>
                      {p.total_received != null ? p.total_received : "-"}
                    </td>

                    <td onClick={() => openProjectDetail(p)}>
                      {p.deadline ? p.deadline.split("T")[0] : "-"}
                    </td>
                    <td onClick={() => openProjectDetail(p)}>
                      {p.status || "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No projects found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* add/edit project modal (unchanged UI, but kept hidden when detail is open) */}
      {showModal && !showDetail && (
        <div
          className="projects-modal-overlay"
          onMouseDown={handleOverlayClick}
        >
          <div
            className="projects-modal"
            ref={modalRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3>{editingProject ? "Edit Project" : "Add New Project"}</h3>
            {message && (
              <div
                className={`projects-message ${
                  message.startsWith("✅") ? "success" : "error"
                }`}
              >
                {message}
              </div>
            )}
            <form onSubmit={handleSubmit} className="project-form">
              <input
                type="text"
                name="name"
                placeholder="Project Name"
                value={form.name}
                onChange={handleChange}
                required
                disabled={saving}
              />
              <select
                name="client_id"
                value={form.client_id}
                onChange={handleChange}
                disabled={saving}
                required
              >
                <option value="">Select Client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <input type="text" value={user?.name || "Unknown"} disabled />
              <input type="hidden" name="manager_id" value={form.manager_id} />

              <textarea
                name="description"
                placeholder="Description"
                value={form.description}
                onChange={handleChange}
                disabled={saving}
              />
              <input
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={handleChange}
                disabled={saving}
              />
              <input
                type="date"
                name="deadline"
                value={form.deadline}
                onChange={handleChange}
                disabled={saving}
              />
              <input
                type="number"
                name="budget"
                placeholder="Budget"
                value={form.budget}
                onChange={handleChange}
                disabled={saving}
              />
              <input
                type="number"
                name="advance_paid"
                placeholder="Advance Paid"
                value={form.advance_paid}
                onChange={handleChange}
                disabled={saving}
              />
              <input
                type="text"
                name="currency"
                placeholder="Currency"
                value={form.currency}
                onChange={handleChange}
                disabled={saving}
              />
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                disabled={saving}
              >
                <option value="planned">Planned</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <textarea
                name="upsellings"
                placeholder="Upsellings"
                value={form.upsellings}
                onChange={handleChange}
                disabled={saving}
              />

              <div className="projects-modal-actions">
                <button type="submit" disabled={saving}>
                  {saving
                    ? editingProject
                      ? "Updating..."
                      : "Adding..."
                    : editingProject
                    ? "Update Project"
                    : "Add Project"}
                </button>
                <button
                  type="button"
                  className="projects-cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProject(null);
                    resetForm();
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* project detail modal with assets management */}
      {showDetail && selectedProject && (
        <div
          className="projects-modal-overlay"
          onMouseDown={handleDetailOverlay}
        >
          <div
            className="projects-modal large"
            ref={detailRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* CLOSE X button (top-right) */}
            <button
              className="modal-close-btn"
              aria-label="Close project details"
              onClick={() => {
                setShowDetail(false);
                setSelectedProject(null);
                setAssets([]);
              }}
            >
              <X size={20} />
            </button>

            {/* Header: title (left) and budget card (right, absolute) */}
            <div className="project-detail-header">
              <div className="project-detail-title">
                <h3 style={{ margin: 0 }}>
                  Project Details — {selectedProject.name}
                </h3>
                <div style={{ marginTop: 8, color: "#374151" }}>
                  <small>
                    {clients.find((c) => c.id === selectedProject.client_id)
                      ?.name || "-"}
                    {" • "}
                    {selectedProject.status || "-"}
                  </small>
                </div>
              </div>

              <div className="project-budget" aria-hidden>
                <div className="project-budget-amount">
                  {formatCurrency(
                    selectedProject.budget,
                    selectedProject.currency
                  )}
                </div>
                <div className="project-budget-sub">
                  {selectedProject.advance_paid != null
                    ? `Advance: ${formatCurrency(
                        selectedProject.advance_paid,
                        selectedProject.currency
                      )}`
                    : "No advance"}
                </div>
              </div>
            </div>

            {message && (
              <div
                className={`projects-message ${
                  message.startsWith("✅") ? "success" : "error"
                }`}
              >
                {message}
              </div>
            )}

            <div className="project-detail-grid" style={{ marginTop: 8 }}>
              <div>
                <strong>ID:</strong> {selectedProject.id}
              </div>
              <div>
                <strong>Client:</strong>{" "}
                {clients.find((c) => c.id === selectedProject.client_id)
                  ?.name || "-"}
              </div>
              <div>
                <strong>Manager:</strong>{" "}
                {selectedProject.manager_id === user.id
                  ? user.name
                  : managers.find((m) => m.id === selectedProject.manager_id)
                      ?.name || "-"}
              </div>
              <div>
                <strong>Status:</strong> {selectedProject.status}
              </div>
              <div>
                <strong>Start:</strong>{" "}
                {selectedProject.start_date
                  ? selectedProject.start_date.split("T")[0]
                  : "-"}
              </div>
              <div>
                <strong>Deadline:</strong>{" "}
                {selectedProject.deadline
                  ? selectedProject.deadline.split("T")[0]
                  : "-"}
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Description:</strong>
                <p>{selectedProject.description || "-"}</p>
              </div>

              <div>
                <strong>Advance Paid:</strong>{" "}
                {selectedProject.advance_paid != null
                  ? formatCurrency(
                      selectedProject.advance_paid,
                      selectedProject.currency
                    )
                  : "-"}
              </div>
              <div>
                <strong>Total Received:</strong>{" "}
                {selectedProject.total_received != null
                  ? formatCurrency(
                      selectedProject.total_received,
                      selectedProject.currency
                    )
                  : "-"}
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Upsellings:</strong>
                <p>{selectedProject.upsellings || "-"}</p>
              </div>
            </div>

            <hr />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <h4 style={{ margin: 0 }}>Assets for this project</h4>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="asset-toggle-btn"
                  onClick={() => setAssetFormOpen((s) => !s)}
                >
                  {assetFormOpen ? (
                    <>
                      <Minus size={16} /> Hide Add Asset
                    </>
                  ) : (
                    <>
                      <Plus size={16} /> Add Asset
                    </>
                  )}
                </button>

                <button
                  className="projects-edit-btn"
                  onClick={() => {
                    handleEdit(selectedProject);
                    setShowDetail(false);
                  }}
                >
                  <Pencil size={16} /> Edit Project
                </button>

                <button
                  className="projects-delete-btn"
                  onClick={() => {
                    if (window.confirm("Delete this project?")) {
                      handleDelete(selectedProject.id);
                      setShowDetail(false);
                    }
                  }}
                >
                  <Trash2 size={16} /> Delete Project
                </button>
              </div>
            </div>

            <div className="assets-section">
              <div
                className={`asset-form-collapsible ${
                  assetFormOpen ? "open" : "closed"
                }`}
              >
                {assetFormOpen && (
                  <form onSubmit={createAsset} className="asset-form">
                    <input
                      name="name"
                      placeholder="Asset name"
                      value={assetForm.name}
                      onChange={handleAssetChange}
                      required
                    />
                    <input
                      name="provider"
                      placeholder="Provider"
                      value={assetForm.provider}
                      onChange={handleAssetChange}
                    />
                    <input
                      type="date"
                      name="purchase_date"
                      value={assetForm.purchase_date}
                      onChange={handleAssetChange}
                    />
                    <input
                      type="date"
                      name="expiry_date"
                      value={assetForm.expiry_date}
                      onChange={handleAssetChange}
                      required
                    />
                    <input
                      type="number"
                      step="0.01"
                      name="cost"
                      placeholder="Cost"
                      value={assetForm.cost}
                      onChange={handleAssetChange}
                    />
                    <input
                      name="currency"
                      placeholder="Currency"
                      value={assetForm.currency}
                      onChange={handleAssetChange}
                    />
                    <select
                      name="status"
                      value={assetForm.status}
                      onChange={handleAssetChange}
                    >
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="archived">Archived</option>
                    </select>
                    <textarea
                      name="notes"
                      placeholder="Notes"
                      value={assetForm.notes}
                      onChange={handleAssetChange}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="submit" disabled={assetSaving}>
                        {assetSaving ? "Adding..." : "Add Asset"}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setAssetForm({
                            name: "",
                            provider: "",
                            purchase_date: "",
                            expiry_date: "",
                            cost: "",
                            currency: "",
                            status: "active",
                            notes: "",
                          })
                        }
                      >
                        Reset
                      </button>
                    </div>
                  </form>
                )}
              </div>

              <div className="assets-list">
                {assets.length === 0 ? (
                  <p>No assets yet for this project.</p>
                ) : (
                  <table className="assets-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Provider</th>
                        <th>Purchase</th>
                        <th>Expiry</th>
                        <th>Cost</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.map((a) => (
                        <tr key={a.id}>
                          <td>{a.name}</td>
                          <td>{a.provider || "-"}</td>
                          <td>
                            {a.purchase_date
                              ? a.purchase_date.split("T")[0]
                              : "-"}
                          </td>
                          <td>
                            {a.expiry_date ? a.expiry_date.split("T")[0] : "-"}
                          </td>
                          <td>
                            {a.cost != null ? a.cost : "-"} {a.currency || ""}
                          </td>
                          <td>{a.status}</td>
                          <td className="assets-notes-cell">
                            {a.notes
                              ? a.notes.slice(0, 60) +
                                (a.notes.length > 60 ? "..." : "")
                              : "-"}
                          </td>
                          <td>
                            <button onClick={() => deleteAsset(a.id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* bottom close removed per request */}
          </div>
        </div>
      )}
    </div>
  );
}
